import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Building2, X } from 'lucide-react'
import { useCreateSite } from '@/lib/api/hooks'
import { Button } from '@/ui/button'
import { cn } from '@/lib/utils'

export function CreateSiteDialog({ onClose }: { onClose: () => void }) {
  const nav = useNavigate()
  const create = useCreateSite()
  const [name, setName] = useState('')
  const [industry, setIndustry] = useState<'BESS' | 'petrochem'>('BESS')
  const [address, setAddress] = useState('')

  const submit = () => {
    if (!name.trim()) return
    const site_id = `SITE-${Math.random().toString(36).slice(2, 7).toUpperCase()}`
    create.mutate(
      { site_id, site_name: name.trim(), industry, address: address.trim() || undefined },
      { onSuccess: (d) => { onClose(); nav(`/site/${d.site_id}/assessment`) } },
    )
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="New site"
        className="w-full max-w-md rounded-xl border bg-popover p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-[17px] font-semibold">New site</h2>
          <button onClick={onClose} aria-label="Close" className="text-faint hover:text-foreground">
            <X className="size-4" />
          </button>
        </div>

        <div className="space-y-3.5">
          <div>
            <label className="mb-1 block text-[12px] font-medium">Site name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submit()}
              autoFocus
              placeholder="e.g. Manchester Battery Hub"
              className="h-9 w-full rounded-md border border-input bg-surface-2 px-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-ring"
            />
          </div>
          <div>
            <label className="mb-1 block text-[12px] font-medium">Industry pack</label>
            <div className="inline-flex overflow-hidden rounded-md border border-input">
              {(
                [
                  ['BESS', 'BESS'],
                  ['petrochem', 'DSEAR (petrochem)'],
                ] as const
              ).map(([v, label]) => (
                <button
                  key={v}
                  onClick={() => setIndustry(v)}
                  className={cn(
                    'px-3 py-1.5 text-[13px] font-medium',
                    industry === v ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:bg-secondary',
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="mb-1 block text-[12px] font-medium">
              Address <span className="text-faint">(optional)</span>
            </label>
            <input
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Site address"
              className="h-9 w-full rounded-md border border-input bg-surface-2 px-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-ring"
            />
          </div>
        </div>

        {create.isError && <p className="mt-2 text-[12px] text-ignition">Couldn&rsquo;t create the site. Try again.</p>}

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" onClick={submit} disabled={!name.trim() || create.isPending}>
            <Building2 /> {create.isPending ? 'Creating…' : 'Create & assess'}
          </Button>
        </div>
      </div>
    </div>
  )
}
